// src/pages/PatientPreviuAgePage.jsx
// Route: /patient/previu-age
//
// Patient-facing Previu Age display.
// Constitution §VI: "Year-on-year Previu Age delta is the single most powerful
// subscriber retention and motivation metric."
//
// Shows: biological age, delta, YoY direction, 5 dimension bars,
// and a plain-language interpretation.
// Uses PatientAppShell for navigation chrome.

import React, { useEffect, useState } from 'react';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import Icon from '../components/Icon';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

// ── Colour helpers ─────────────────────────────────────────────────────────

function deltaColor(deltaVal) {
  if (deltaVal == null) return 'var(--amber)';
  if (parseFloat(deltaVal) <= -2) return 'var(--green)';
  if (parseFloat(deltaVal) <= 1)  return 'var(--amber)';
  return 'var(--red)';
}

function deltaLabel(deltaVal) {
  if (deltaVal == null) return 'Calculating…';
  const d = parseFloat(deltaVal);
  if (d <= -2) return 'Your body is ageing more slowly than your calendar age — keep it up.';
  if (d <=  1) return 'Your biological age is on track with your calendar age.';
  if (d <=  4) return 'Your body is ageing slightly faster than your calendar age. Focused changes can reverse this.';
  return 'Your body is ageing significantly faster than your calendar age. Your next visit is important.';
}

function yoyLabel(direction) {
  if (direction === 'IMPROVING')    return '↓ Improving since last year';
  if (direction === 'DETERIORATING') return '↑ Higher than last year';
  if (direction === 'STABLE')       return '→ Stable';
  return null;
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function PatientPreviuAgePage() {
  const { patient } = usePatient();
  const [score,   setScore]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);

  useEffect(() => {
    if (!patient?.id) return;
    const token = localStorage.getItem('previu_token') || localStorage.getItem('token');
    fetch(`${API_BASE}/previu-age/${patient.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.status === 204 ? null : r.json())
      .then(d => { setScore(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [patient?.id]);

  const previuAge  = score?.previuAgeYears  != null ? parseFloat(score.previuAgeYears).toFixed(1)  : null;
  // Parse explicitly to number — chronologicalAgeYears is Integer from Java.
  // Without parseFloat, JS arithmetic can produce NaN if the field
  // arrives as a string or undefined, silently breaking dimDelta.
  const chronAge   = score?.chronologicalAgeYears != null
    ? parseFloat(score.chronologicalAgeYears) : null;
  const deltaVal   = score?.ageDeltaYears   != null ? parseFloat(score.ageDeltaYears).toFixed(1)   : null;
  const yoyVal     = score?.yoyDeltaYears   != null ? parseFloat(score.yoyDeltaYears).toFixed(1)   : null;
  const direction  = score?.yoyDeltaDirection;

  const dimensions = score ? [
    { label: 'Metabolic',      val: score.metabolicAge,      desc: 'Blood sugar, cholesterol, insulin sensitivity' },
    { label: 'Cardiovascular', val: score.cardiovascularAge, desc: 'Heart, circulation, inflammation' },
    { label: 'Organ',          val: score.organAge,          desc: 'Kidney, liver, thyroid function' },
    { label: 'Nutritional',    val: score.nutritionalAge,    desc: 'Vitamins, minerals, haemoglobin' },
    { label: 'Lifestyle',      val: score.lifestyleAge,      desc: 'Metabolic proxy — wearable inputs pending' },
  ].filter(d => d.val != null) : [];

  return (
    <PatientAppShell>
      <div style={{
        maxWidth: 560, margin: '0 auto',
        padding: '24px 0 48px',
        fontFamily: 'var(--font-body)',
      }}>
        <div style={{
          fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '.1em', color: 'var(--text-muted)', marginBottom: 4,
        }}>
          Previu Age
        </div>
        <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 24 }}>
          Your biological age
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 14 }}>
            Computing your Previu Age…
          </div>
        )}

        {!loading && (error || !score) && (
          <div style={{
            background: 'var(--bg-subtle)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '28px 24px', textAlign: 'center',
          }}>
            <div className="empty-icon empty-icon--badge" aria-hidden="true">
            <Icon name="trend" size={22} />
          </div>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
              Score not available yet
            </div>
            <div style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Your Previu Age score appears after your second lab test.
              Complete your next blood draw to unlock your biological age trend.
            </div>
          </div>
        )}

        {!loading && score && (
          <>
            {/* ── Score card ─────────────────────────────────────────────── */}
            <div style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: '28px 24px', marginBottom: 16,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, marginBottom: 20 }}>
                {/* Biological age */}
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>
                    Biological age
                  </div>
                  <div style={{
                    fontSize: 56, fontWeight: 500, lineHeight: 1,
                    fontFamily: 'var(--font-display)',
                    color: 'var(--text-primary)',
                  }}>
                    {previuAge ?? '—'}
                    <span style={{ fontSize: 20, fontWeight: 400, marginLeft: 4, color: 'var(--text-muted)' }}>
                      yrs
                    </span>
                  </div>
                  {chronAge != null && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                      Calendar age: {chronAge} yrs
                    </div>
                  )}
                </div>

                {/* Delta */}
                {deltaVal != null && (
                  <div style={{ paddingBottom: 8 }}>
                    <div style={{
                      fontSize: 28, fontWeight: 500, lineHeight: 1,
                      color: deltaColor(deltaVal),
                    }}>
                      {parseFloat(deltaVal) >= 0 ? `+${deltaVal}` : deltaVal} yrs
                    </div>
                    <div style={{ fontSize: 11, color: deltaColor(deltaVal), marginTop: 3 }}>
                      {parseFloat(deltaVal) <= -2 ? 'Biologically younger' :
                       parseFloat(deltaVal) <= 1  ? 'On track' : 'Biologically older'}
                    </div>
                  </div>
                )}
              </div>

              {/* Plain-language interpretation */}
              <div style={{
                background: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)',
                padding: '12px 14px', fontSize: 13, color: 'var(--text-secondary)',
                lineHeight: 1.6, marginBottom: yoyVal ? 14 : 0,
              }}>
                {deltaLabel(deltaVal)}
              </div>

              {/* YoY */}
              {direction && yoyLabel(direction) && (
                <div style={{
                  marginTop: 10, fontSize: 12,
                  color: direction === 'IMPROVING' ? 'var(--green)'
                    : direction === 'DETERIORATING' ? 'var(--red)'
                    : 'var(--text-muted)',
                }}>
                  {yoyLabel(direction)}
                  {yoyVal && ` (${Math.abs(yoyVal)} yrs)`}
                </div>
              )}
            </div>

            {/* ── Dimension bars ──────────────────────────────────────────── */}
            {dimensions.length > 0 && (
              <div style={{
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)', padding: '20px 24px', marginBottom: 16,
              }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16,
                }}>
                  By dimension
                </div>
                {dimensions.map(dim => {
                  const dimVal     = parseFloat(dim.val).toFixed(1);
                  // Keep dimDeltaNum as a number (not string) — .toFixed() returns a
                  // string which causes parseFloat comparisons to silently fail in
                  // some JS engines, producing incorrect bar colours.
                  const dimDeltaNum = chronAge != null
                    ? parseFloat(dim.val) - chronAge : null;
                  const dimDelta    = dimDeltaNum != null ? dimDeltaNum.toFixed(1) : null;
                  // Colour thresholds — explicit Number() guards against string coercion:
                  //   <= 1  → green  (on track or biologically younger)
                  //   1–3   → amber  (mildly elevated)
                  //   > 3   → red    (clinically significant, must be visually distinct)
                  const barColor = dimDeltaNum == null
                    ? 'var(--text-muted)'
                    : dimDeltaNum <= 1
                      ? 'var(--green)'
                      : dimDeltaNum <= 3
                        ? 'var(--amber)'
                        : 'var(--red)';
                  const pct = dimDeltaNum != null
                    ? Math.min(Math.max((dimDeltaNum + 10) / 20 * 100, 0), 100)
                    : 50;

                  return (
                    <div key={dim.label} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                        <div>
                          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                            {dim.label}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                            {dim.desc}
                          </span>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 500, color: barColor }}>
                          {dimVal} yrs
                          {dimDelta != null && (
                            <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 4 }}>
                              ({parseFloat(dimDelta) >= 0 ? '+' : ''}{dimDelta})
                            </span>
                          )}
                        </span>
                      </div>
                      <div style={{
                        height: 6, borderRadius: 3,
                        background: 'var(--bg-subtle)',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          height: '100%', width: `${pct}%`,
                          background: barColor, borderRadius: 3,
                          transition: 'width .4s ease',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Metadata ────────────────────────────────────────────────── */}
            <div style={{
              fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6,
              fontStyle: 'italic', padding: '0 4px',
            }}>
              Based on {score.drawsUsed} blood draw{score.drawsUsed !== 1 ? 's' : ''}.
              Lifestyle score estimated from metabolic proxy — wearable + symptom inputs pending.
            </div>
          </>
        )}
      </div>
    </PatientAppShell>
  );
}
